import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Trash2, Pencil, FileDown, FileText, ArrowLeftRight, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, EmptyState, Segmented } from "./ui";
import { EditBuyDialog, EditSellDialog } from "./Dialogs";
import useRefreshOnReturn from "../lib/useRefreshOnReturn";
import TimeFilterBar from "./TimeFilterBar";
import { useFilter } from "../context/FilterContext";
import { api, downloadExport } from "../lib/api";
import { num, shortDate, money, gainColor, isCryptoType } from "../lib/format";
import { useMoney } from "../context/MoneyContext";
import { useTranslation } from "react-i18next";

const TABS = [
  { value: "all", key: "transactions.all" }, { value: "buy", key: "transactions.buys" }, { value: "sell", key: "transactions.sells" },
];

// Quantity, price, and fee each get their own column instead of being crammed
// into one "qty @ price" cell.
const TX_GRID = "grid-cols-[100px_60px_1fr_100px_110px_84px_140px_110px_64px]";
const PAGE_SIZE = 50;

// Collapsed-by-default transaction history — folded into Holdings (rather than its
// own page) to cut down on repeated space across Dashboard/Holdings/Analytics.
export default function TransactionsPanel({ alwaysOpen = false }) {
  const { t } = useTranslation();
  const { display } = useMoney();
  const { dates } = useFilter();
  const [open, setOpen] = useState(alwaysOpen);
  const [tab, setTab] = useState("all");
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBuyTx, setEditBuyTx] = useState(null);
  const [editSellTx, setEditSellTx] = useState(null);
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [instrumentFilter, setInstrumentFilter] = useState("all");
  const [page, setPage] = useState(0);

  const load = useCallback((silent) => {
    if (!silent) setLoading(true);
    const params = {};
    if (dates.start) params.start = dates.start;
    if (dates.end) params.end = dates.end;
    if (tab !== "all") params.tx_type = tab;
    api.transactions(params).then((r) => setTxs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [dates.start, dates.end, tab]);
  useEffect(() => { if (open) load(); }, [load, open]);
  useRefreshOnReturn(() => { if (open) load(true); });

  // Options come from the loaded transactions themselves, so only brokers/instruments
  // actually used show up — not every broker or instrument that exists in general.
  const brokerOptions = useMemo(() => Array.from(new Set(txs.map((t) => t.broker).filter(Boolean))).sort(), [txs]);
  const instrumentOptions = useMemo(() => Array.from(new Set(txs.map((t) => t.ticker).filter(Boolean))).sort(), [txs]);
  useEffect(() => {
    if (brokerFilter !== "all" && !brokerOptions.includes(brokerFilter)) setBrokerFilter("all");
    if (instrumentFilter !== "all" && !instrumentOptions.includes(instrumentFilter)) setInstrumentFilter("all");
  }, [brokerOptions, instrumentOptions, brokerFilter, instrumentFilter]);
  const filteredTxs = useMemo(() => txs.filter((t) =>
    (brokerFilter === "all" || t.broker === brokerFilter) &&
    (instrumentFilter === "all" || t.ticker === instrumentFilter)
  ), [txs, brokerFilter, instrumentFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredTxs.length / PAGE_SIZE));
  useEffect(() => { if (page > pageCount - 1) setPage(0); }, [pageCount, page]);
  const pagedTxs = filteredTxs.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const del = async (id) => {
    if (!window.confirm(t("transactions.deleteConfirm"))) return;
    try { await api.deleteTransaction(id); toast.success("Transaction deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const doExport = async (fmt) => {
    try {
      const p = {}; if (dates.start) p.start = dates.start; if (dates.end) p.end = dates.end;
      await downloadExport("transactions", fmt, p); toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div>
      {!alwaysOpen && (
        <button data-testid="toggle-transactions" onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors">
          <ArrowLeftRight size={15} /> {t("transactions.title")}
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}

      {open && (
        <Panel className={`card overflow-hidden ${alwaysOpen ? "" : "mt-3"}`} testid="transactions-panel">
          <div className="p-4 sm:p-6 space-y-4 border-b border-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Segmented options={TABS.map((tb) => ({ value: tb.value, label: t(tb.key) }))} value={tab} onChange={(v) => { setTab(v); setPage(0); }} testid="tx-tabs" />
              <div className="flex items-center gap-2">
                <button data-testid="export-tx-csv" className="btn-outline" onClick={() => doExport("csv")}><FileDown size={16} /> CSV</button>
                <button data-testid="export-tx-pdf" className="btn-outline" onClick={() => doExport("pdf")}><FileText size={16} /> PDF</button>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <select data-testid="tx-broker-filter" className="input py-2 w-auto" value={brokerFilter} onChange={(e) => { setBrokerFilter(e.target.value); setPage(0); }}>
                  <option value="all">{t("transactions.allBrokers")}</option>
                  {brokerOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <select data-testid="tx-instrument-filter" className="input py-2 w-auto" value={instrumentFilter} onChange={(e) => { setInstrumentFilter(e.target.value); setPage(0); }}>
                  <option value="all">{t("transactions.allInstruments")}</option>
                  {instrumentOptions.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <TimeFilterBar />
            </div>
          </div>

          {loading ? (
            <div className="space-y-3 p-6">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : filteredTxs.length === 0 ? (
            <EmptyState testid="tx-empty" title={t("transactions.emptyTitle")} description={t("transactions.emptyDesc")} />
          ) : (
            <>
              <div className="overflow-x-auto">
              <div className="min-w-[1060px]">
              <div className={`grid ${TX_GRID} gap-3 px-6 py-4 border-b border-slate-100 label`}>
                <div>{t("transactions.date")}</div>
                <div>{t("transactions.type")}</div>
                <div>{t("transactions.instrument")}</div>
                <div className="text-right">{t("transactions.quantity")}</div>
                <div className="text-right">{t("transactions.price")}</div>
                <div className="text-right">{t("transactions.fee")}</div>
                <div className="text-right">{t("transactions.valueUsd")}</div>
                <div>{t("transactions.broker")}</div>
                <div></div>
              </div>
              {pagedTxs.map((tx) => (
                <div key={tx.id} data-testid={`tx-row-${tx.id}`} className={`grid ${TX_GRID} gap-3 px-6 py-4 items-center border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors text-sm`}>
                  <div className="tabular">{shortDate(tx.date)}</div>
                  <div>
                    <span className={`pill ${tx.type === "buy" ? "bg-brand-50 text-brand-600" : "bg-amber-50 text-amber-700"}`}>{tx.type.toUpperCase()}</span>
                  </div>
                  <div>
                    <Link to={`/instrument/${tx.ticker}`} className="font-medium text-slate-900 hover:text-brand-600">{tx.ticker}</Link>
                    <div className="text-xs text-slate-400 truncate max-w-[140px]">{tx.name}</div>
                  </div>
                  <div className="text-right tabular">
                    {num(tx.quantity, 4, { crypto: isCryptoType(tx.asset_type) })}
                    {tx.type === "buy" && tx.remaining_quantity < tx.quantity && (
                      <div className="text-xs text-slate-400">{t("transactions.open", { count: num(tx.remaining_quantity, 4, { crypto: isCryptoType(tx.asset_type) }) })}</div>
                    )}
                  </div>
                  <div className="text-right tabular">{money(tx.price, tx.currency, { crypto: isCryptoType(tx.asset_type) })}</div>
                  <div className="text-right tabular text-slate-500">{tx.fee > 0 ? money(tx.fee, tx.currency) : "—"}</div>
                  <div className="text-right tabular font-medium">
                    {display(tx.value_usd, { crypto: isCryptoType(tx.asset_type) }).primary}
                    {display(tx.value_usd, { crypto: isCryptoType(tx.asset_type) }).secondary && <div className="text-xs font-normal text-slate-400">≈ {display(tx.value_usd, { crypto: isCryptoType(tx.asset_type) }).secondary}</div>}
                    {tx.type === "sell" && (
                      <div className={`text-xs ${gainColor(tx.realized_gain_usd)}`}>P/L {display(tx.realized_gain_usd, { crypto: isCryptoType(tx.asset_type) }).primary}</div>
                    )}
                  </div>
                  <div className="text-slate-600">{tx.broker || "—"}</div>
                  <div className="flex flex-wrap justify-end gap-1">
                    <button data-testid={`tx-edit-${tx.id}`} onClick={() => (tx.type === "buy" ? setEditBuyTx(tx) : setEditSellTx(tx))} className="text-slate-300 hover:text-brand-600 transition-colors p-1.5"><Pencil size={15} /></button>
                    <button data-testid={`tx-delete-${tx.id}`} onClick={() => del(tx.id)} className="text-slate-300 hover:text-rose-500 transition-colors p-1.5"><Trash2 size={16} /></button>
                  </div>
                  {tx.notes && <div className="col-span-full text-xs text-slate-500">📝 {tx.notes}</div>}
                </div>
              ))}
              </div>
              </div>
              {pageCount > 1 && (
                <div className="flex items-center justify-between px-6 py-4">
                  <span className="text-sm text-slate-400">{t("common.pageOf", { page: page + 1, pages: pageCount })}</span>
                  <div className="flex gap-2">
                    <button data-testid="tx-page-prev" className="btn-outline py-1.5 px-3 text-sm disabled:opacity-40" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                      <ChevronLeft size={15} /> {t("common.previous")}
                    </button>
                    <button data-testid="tx-page-next" className="btn-outline py-1.5 px-3 text-sm disabled:opacity-40" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
                      {t("common.next")} <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      <EditBuyDialog open={!!editBuyTx} tx={editBuyTx} onClose={() => setEditBuyTx(null)} onDone={load} />
      <EditSellDialog open={!!editSellTx} tx={editSellTx} onClose={() => setEditSellTx(null)} onDone={load} />
    </div>
  );
}
